import React from 'react';
import { UserPlus, Users } from 'lucide-react';

interface ParticipantAvatarProps {
    identity: string;
    name?: string;
    isLocal?: boolean;
}

const ParticipantAvatar: React.FC<{ participant: ParticipantAvatarProps }> = ({ participant }) => {
    const displayName = participant.name || participant.identity;
    const initials = displayName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
    const avatarColors = ['from-gray-600 to-gray-800', 'from-gray-700 to-gray-900', 'from-slate-600 to-slate-800', 'from-zinc-600 to-zinc-800', 'from-neutral-600 to-neutral-800', 'from-stone-600 to-stone-800'];
    const colorIndex = participant.identity ? participant.identity.charCodeAt(0) % avatarColors.length : 0;
    const avatarGradient = avatarColors[colorIndex];
    const isLocal = participant.isLocal;

    return (
        <div
            key={participant.identity}
            className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br ${avatarGradient} flex items-center justify-center relative`}
            title={`${displayName}${isLocal ? ' (You)' : ''}`}
        >
            <span className="text-white text-xs font-bold">{initials}</span>
            {isLocal && (
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-gray-900" />
            )}
        </div>
    );
};

interface MobileGridProps {
    participants: any[];
    renderParticipantCard: (participant: any) => React.ReactNode;
    onToggleParticipantsSidebar: () => void;
}

const MobileGrid: React.FC<MobileGridProps> = ({
    participants,
    renderParticipantCard,
    onToggleParticipantsSidebar,
}) => {
    if (participants.length === 0) return null;

    const firstParticipant = participants[0];
    const remainingParticipants = participants.slice(1);
    const remainingCount = remainingParticipants.length;

    return (
        <div className="flex flex-col gap-2 h-full">
            {/* Top full-width video */}
            <div className="flex-shrink-0">
                {renderParticipantCard(firstParticipant)}
            </div>

            {remainingCount > 0 && (
                <div className="flex gap-2 flex-1 min-h-0">
                    {/* Left column: second participant video */}
                    {remainingCount >= 1 && (
                        <div className="flex-1 min-w-0">
                            {renderParticipantCard(remainingParticipants[0])}
                        </div>
                    )}

                    {/* Right column: grouped avatars + plus icon */}
                    {remainingCount >= 2 && (
                        <div
                            className="flex-1 bg-gray-900/80 rounded-xl flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-gray-900 transition-colors p-3"
                            onClick={onToggleParticipantsSidebar}
                        >
                            <div className="flex flex-wrap items-center justify-center gap-2">
                                {remainingParticipants.slice(1, 3).map((p) => (
                                    <ParticipantAvatar key={p.identity} participant={p} />
                                ))}
                            </div>
                            {remainingCount > 3 && (
                                <div className="flex items-center gap-1 text-white/80 text-xs font-medium">
                                    <UserPlus className="w-3.5 h-3.5" />
                                    <span>+{remainingCount - 3} more</span>
                                </div>
                            )}
                            {remainingCount === 3 && (
                                <div className="flex items-center gap-1 text-white/80 text-xs font-medium">
                                    <Users className="w-3.5 h-3.5" />
                                    <span>View all</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Only 1 remaining - fill right column with a "more" tile */}
                    {remainingCount === 1 && (
                        <div
                            className="flex-1 bg-gray-900/80 rounded-xl flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-gray-900 transition-colors p-3"
                            onClick={onToggleParticipantsSidebar}
                        >
                            <Users className="w-6 h-6 text-white/60" />
                            <span className="text-white/80 text-xs font-medium">All participants ({participants.length})</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MobileGrid;

export type { ParticipantAvatarProps };
